import { useState, useRef } from 'react';
import { Upload, AlertCircle, CheckCircle, X, Download, HelpCircle } from 'lucide-react';
import { parseCSVToTransactions, generateCSVTemplate } from '../utils/csvUtils';
import { Transaction } from '../types';

interface ImportModalProps {
  onImport: (transactions: Omit<Transaction, 'id'>[]) => void;
  onCancel: () => void;
}

export default function ImportModal({ onImport, onCancel }: ImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<Omit<Transaction, 'id'>[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setSuccess(null);
    parseCSVFile(selectedFile);
  };

  const parseCSVFile = async (csvFile: File) => {
    setIsParsing(true);
    setError(null);

    try {
      const text = await csvFile.text();
      const transactions = parseCSVToTransactions(text);
      
      if (transactions.length === 0) {
        setError('No valid transactions found in the CSV file');
        setPreviewData([]);
      } else {
        setPreviewData(transactions);
        setSuccess(`Successfully parsed ${transactions.length} transaction(s)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
      setPreviewData([]);
    } finally {
      setIsParsing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.name.toLowerCase().endsWith('.csv')) {
      setFile(droppedFile);
      parseCSVFile(droppedFile);
    } else {
      setError('Please drop a CSV file');
    }
  };

  const handleImport = () => {
    if (previewData.length > 0) {
      onImport(previewData);
      onCancel();
    }
  };

  const downloadTemplate = () => {
    const template = generateCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Import Transactions</h2>
          <p className="text-sm text-gray-400">Upload a CSV file to import transactions</p>
        </div>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 mb-6 text-center transition-colors ${
          file
            ? 'border-blue-500/50 bg-blue-500/10'
            : 'border-gray-600 hover:border-gray-500 hover:bg-gray-800/30'
        }`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="p-4 bg-gray-700/50 rounded-full">
            <Upload className="w-8 h-8 text-gray-400" />
          </div>
          
          <div>
            <p className="text-white font-medium mb-1">
              {file ? file.name : 'Drag & drop your CSV file here'}
            </p>
            <p className="text-sm text-gray-400">
              {file ? 'Click to select a different file' : 'or click to browse files'}
            </p>
          </div>
          
          <p className="text-xs text-gray-500">
            Supported format: CSV (Max 10MB)
          </p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-red-400 font-medium">Import Error</p>
              <p className="text-sm text-red-400/80">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-green-400 font-medium">File Ready</p>
              <p className="text-sm text-green-400/80">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* CSV Template Help */}
      <div className="mb-6 p-4 bg-gray-800/30 border border-gray-700/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <HelpCircle className="w-5 h-5 text-blue-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-white font-medium mb-2">CSV Format Requirements</p>
            <ul className="text-sm text-gray-400 space-y-1 mb-3">
              <li>• Required columns: <code className="bg-gray-700 px-1 py-0.5 rounded">description</code>, <code className="bg-gray-700 px-1 py-0.5 rounded">amount</code>, <code className="bg-gray-700 px-1 py-0.5 rounded">type</code> (income/expense), <code className="bg-gray-700 px-1 py-0.5 rounded">category</code>, <code className="bg-gray-700 px-1 py-0.5 rounded">date</code></li>
              <li>• Optional columns: <code className="bg-gray-700 px-1 py-0.5 rounded">paymentMethod</code>, <code className="bg-gray-700 px-1 py-0.5 rounded">tags</code> (semicolon separated)</li>
              <li>• Date format: YYYY-MM-DD (e.g., 2024-01-15)</li>
            </ul>
            <button
              onClick={downloadTemplate}
              className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              <span>Download CSV Template</span>
            </button>
          </div>
        </div>
      </div>

      {/* Preview Table */}
      {previewData.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Preview ({previewData.length} transactions)</h3>
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-64">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr className="text-left text-sm text-gray-400">
                    <th className="py-3 px-4 font-medium">Type</th>
                    <th className="py-3 px-4 font-medium">Description</th>
                    <th className="py-3 px-4 font-medium">Category</th>
                    <th className="py-3 px-4 font-medium">Amount</th>
                    <th className="py-3 px-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {previewData.slice(0, 5).map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-700/30 transition-colors">
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          transaction.type === 'income'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-300">
                        {transaction.description}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs bg-gray-700/50 text-gray-300 px-2 py-1 rounded">
                          {transaction.category}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${
                          transaction.type === 'income' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {transaction.date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {previewData.length > 5 && (
              <div className="py-3 px-4 text-center border-t border-gray-700/50">
                <p className="text-sm text-gray-400">
                  ... and {previewData.length - 5} more transaction(s)
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-4 border-t border-gray-800">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 px-4 bg-gray-700/50 text-gray-300 rounded-lg font-medium hover:bg-gray-700 transition-all"
        >
          Cancel
        </button>
        
        <button
          type="button"
          onClick={handleImport}
          disabled={previewData.length === 0 || isParsing}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 ${
            previewData.length === 0 || isParsing
              ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
          }`}
        >
          {isParsing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Parsing...</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              <span>Import {previewData.length > 0 ? `(${previewData.length})` : ''}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}